import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import { getErrorMessage } from '@shared/api';
import { Alert, AlertDescription, Button, ConfirmDialog } from '@shared/ui';
import { useAdminUserDeletionStatus, useDeleteAdminUser } from '../api/hooks';

const DeletionContext = createContext<((accountId: string) => void) | null>(null);

/** The page owns operation feedback independently of a disappearing target account. */
export function AccountDeletionBoundary({
  children,
  onChanged,
}: PropsWithChildren<{ onChanged: () => void }>) {
  const ability = useAbility();
  const { user, permissions } = useAuth();
  if (!user || !ability.can('manage', 'all')) return children;
  const scope = `molchanovs:${user.id}:${user.systemRole}:${JSON.stringify(permissions ?? [])}`;
  return (
    <AuthorizedDeletionBoundary key={scope} scope={scope} onChanged={onChanged}>
      {children}
    </AuthorizedDeletionBoundary>
  );
}

function AuthorizedDeletionBoundary({
  children,
  scope,
  onChanged,
}: PropsWithChildren<{ scope: string; onChanged: () => void }>) {
  const { t } = useTranslation();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const completedOperation = useRef<string | null>(null);
  const deletion = useDeleteAdminUser();
  const status = useAdminUserDeletionStatus(operationId, scope);
  useEffect(() => {
    if (
      !status.isError &&
      status.data?.status === 'completed' &&
      completedOperation.current !== operationId
    ) {
      completedOperation.current = operationId;
      onChanged();
    }
  }, [status.isError, status.data?.status, operationId, onChanged]);
  const submit = async () => {
    if (!selectedAccountId || deletion.isPending) return;
    try {
      const receipt = await deletion.mutateAsync(selectedAccountId);
      setOperationId(receipt.operationId);
      setSelectedAccountId(null);
      setError(null);
      onChanged();
    } catch (cause) {
      setError(getErrorMessage(cause));
      setSelectedAccountId(null);
    }
  };
  return (
    <DeletionContext.Provider value={setSelectedAccountId}>
      {operationId ? (
        <Alert role="status">
          <AlertDescription>
            {status.isError
              ? t('admin.userDeletion.statusUnavailable')
              : t(`admin.userDeletion.status.${status.data?.status ?? 'queued'}`)}
            {!status.isError && status.data?.message ? ` ${status.data.message}` : null}
          </AlertDescription>
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      {children}
      <ConfirmDialog
        open={selectedAccountId !== null}
        title={t('admin.deleteConfirmTitle')}
        description={t('admin.deleteConfirmDescription')}
        confirmLabel={t('admin.deleteConfirm')}
        cancelLabel={t('admin.deleteCancel')}
        onConfirm={() => void submit()}
        onCancel={() => {
          setSelectedAccountId(null);
        }}
        onOpenChange={(open) => {
          if (!open && !deletion.isPending) setSelectedAccountId(null);
        }}
        confirmButtonProps={{ disabled: deletion.isPending }}
      />
    </DeletionContext.Provider>
  );
}

/** Receives only an already-resolved canonical account UUID, never a profile UUID. */
export function AccountDeletionButton({ accountId }: { accountId: string }) {
  const selectAccount = useContext(DeletionContext);
  const { t } = useTranslation();
  if (!selectAccount) return null;
  return (
    <Button
      variant="destructive"
      size="sm"
      onClick={() => {
        selectAccount(accountId);
      }}
    >
      {t('admin.delete')}
    </Button>
  );
}
